import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetLanguages } from "~/api/languages/languages";
import { Language, UserRole } from "~/api/model";
import {
  getGetOrganizationQueryKey,
  getGetOrganizationSettingsQueryKey,
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganization,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { UpdateButton, UpdateButtonRef } from "~/components/UpdateButton";
import {
  PipelineMode,
  PipelineModeSelector,
} from "~/features/cases/components/PipelineModeSelector";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { trackEvent } from "~/utils/analytics";

type FormData = {
  language: Language;
};

export const SettingsOrganization: FC = () => {
  useTrackPageVisit("app/settings_organization/visit");

  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: organization } = useGetOrganization(organizationId);
  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const { data: languages } = useGetLanguages();
  const { mutate, isPending } = useUpdateOrganization();
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateOrganizationSettings();
  const updateButtonRef = useRef<UpdateButtonRef>(null);
  const queryClient = useQueryClient();

  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_TOC");

  const { handleSubmit, setValue, watch } = useForm<FormData>();

  const currentLanguage = watch("language");

  const onSubmit = (data: FormData) => {
    trackEvent({
      eventName: "app/settings_organization/language.select/change",
      properties: { language: data.language },
    });

    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.save",
      properties: { mode: selectedPipelineMode },
    });

    mutate(
      { organizationId, data: { language: data.language } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        },
      }
    );

    updateSettings(
      { organizationId, data: { pipelineMode: selectedPipelineMode } },
      {
        onSuccess: () => {
          updateButtonRef.current?.onSuccess();
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationSettingsQueryKey(organizationId),
          });
        },
      }
    );
  };

  const handlePipelineModeChange = (mode: PipelineMode) => {
    setSelectedPipelineMode(mode);
    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.select",
      properties: { mode },
    });
  };

  useEffect(() => {
    if (organization?.language) {
      setValue("language", organization.language);
    }
  }, [organization?.language, setValue]);

  useEffect(() => {
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(organizationSettings.pipelineMode);
    }
  }, [organizationSettings?.pipelineMode]);

  const handleLanguageChange = (value: string) => {
    setValue("language", value as Language);
  };

  const currentLanguageLabel =
    currentLanguage && languages
      ? languages[currentLanguage]?.label
      : undefined;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-6 mt-2 w-full max-w-[766px]"
    >
      <h3 className="text-2xl font-semibold">
        <FormattedMessage
          id="settings.organization"
          defaultMessage="Organization"
        />
      </h3>

      {/* Output Language Section */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="organizationLanguage">
          <FormattedMessage
            id="settings.organization.outputLanguage"
            defaultMessage="Output Language"
          />
        </Label>
        <Select value={currentLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger
            id="organizationLanguage"
            onClick={() => {
              trackEvent({
                eventName: "app/settings_organization/language.select/click",
              });
            }}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                id: "settings.organization.selectLanguage",
                defaultMessage: "Select a language",
              })}
            >
              {currentLanguageLabel}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languages &&
              Object.entries(languages).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.organization.outputLanguageHint"
            defaultMessage="This language will be used for AI-generated content such as summaries, classifications, and chat responses."
          />
        </span>
      </div>

      {/* Pipeline Mode Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[UserRole.OWNER, UserRole.SUPERADMIN]}>
        <div className="flex flex-col gap-4 border-t pt-6">
          <div>
            <Label>
              <FormattedMessage
                id="settings.organization.pipelineMode.title"
                defaultMessage="Document Processing Mode"
              />
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              <FormattedMessage
                id="settings.organization.pipelineMode.description"
                defaultMessage="Choose how Sky AI handles document structure and organization for new uploads."
              />
            </p>
          </div>

          <PipelineModeSelector
            selectedMode={selectedPipelineMode}
            onModeChange={handlePipelineModeChange}
            variant="detailed"
          />
        </div>
      </DisplayFor>

      <UpdateButton
        isPending={isPending || isUpdatingSettings}
        ref={updateButtonRef}
      />
    </form>
  );
};
